import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { apiGetLeastOwnedCards, apiGetCard } from '../../../api/cardApi';
import type { LeastOwnedCard, Card, CardRarity } from '../../../types/models/card';
import CardDetailModal from '../../Cards/CardDetailModal';
import { useAppSelector } from '../../../store/hooks';

const storageUrl = process.env.REACT_APP_STORAGE_URL ?? '';
const toImgSrc = (path: string | null | undefined) =>
  path ? (path.startsWith('http') ? path : storageUrl + path) : null;

const RARITY_COLOR: Record<CardRarity, string> = {
  common: 'rgba(255,255,255,0.55)',
  rare: '#4a9eff',
  epic: '#c27ff5',
  legendary: 'gold',
};

// ─── Styled ───────────────────────────────────────────────────────────────────

const Container = styled.div`
  padding: 28px 16px 80px;
  min-height: calc(100vh - 70px);
`;

const PageHeader = styled.div`
  margin-bottom: 24px;
  text-align: center;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 900;
  color: #f43f5e;
  margin: 0 0 6px;
`;

const PageSub = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.4);
  margin: 0;
  line-height: 1.5;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
`;

const CardWrapper = styled.div`
  background: #111;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.07);
  cursor: pointer;
  position: relative;
  active: {
    opacity: 0.8;
  }
`;

const RarityBar = styled.div<{ $color: string }>`
  height: 3px;
  background: ${({ $color }) => $color};
  width: 100%;
`;

const CardImgArea = styled.div`
  aspect-ratio: 2 / 3;
  background: #0d0d0d;
  overflow: hidden;
  position: relative;
`;

const CardImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
`;

const CardImgPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.1);
`;

const OwnersCountBadge = styled.div<{ $zero: boolean }>`
  position: absolute;
  top: 6px;
  right: 6px;
  background: ${({ $zero }) => ($zero ? 'rgba(244,63,94,0.85)' : 'rgba(0,0,0,0.75)')};
  border: 1px solid ${({ $zero }) => ($zero ? 'transparent' : 'rgba(255,255,255,0.12)')};
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 20px;
  backdrop-filter: blur(4px);
`;

const CardInfo = styled.div`
  padding: 8px 10px 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RarityDot = styled.div<{ $color: string }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
  display: inline-block;
  margin-right: 5px;
  vertical-align: middle;
`;

const RarityText = styled.span<{ $color: string }>`
  font-size: 10px;
  font-weight: 700;
  color: ${({ $color }) => $color};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  vertical-align: middle;
`;

const OwnersList = styled.div`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.35);
  line-height: 1.4;
`;

const LoadingText = styled.p`
  color: rgba(255, 255, 255, 0.4);
  text-align: center;
  padding: 40px 0;
  font-size: 14px;
`;

// ─── Component ────────────────────────────────────────────────────────────────

const LeastOwnedCardsMobile: React.FC = () => {
  const userId = useAppSelector((s) => s.auth.userLoggedState?.id ?? s.auth.user?.id ?? null);
  const isAuthenticated = useAppSelector(
    (s) => s.auth.isAuthenticated || s.auth.userLoggedState !== null
  );

  const [cards, setCards] = useState<LeastOwnedCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailCard, setDetailCard] = useState<Card | null>(null);
  const [loadingDetailId, setLoadingDetailId] = useState<number | null>(null);
  const [cardCache, setCardCache] = useState<Record<number, Card>>({});

  useEffect(() => {
    apiGetLeastOwnedCards()
      .then((res) => setCards(res))
      .catch(() => setCards([]))
      .finally(() => setLoading(false));
  }, []);

  const openDetail = async (card: LeastOwnedCard) => {
    if (loadingDetailId === card.id) return;
    if (cardCache[card.id]) { setDetailCard(cardCache[card.id]); return; }
    setLoadingDetailId(card.id);
    try {
      const full = await apiGetCard(card.id);
      setCardCache((prev) => ({ ...prev, [card.id]: full }));
      setDetailCard(full);
    } catch { /* noop */ } finally {
      setLoadingDetailId(null);
    }
  };

  const getOwnedQty = (card: LeastOwnedCard) => {
    if (!userId) return 0;
    return card.owners.some((o) => o.id === userId) ? 1 : 0;
  };

  const formatOwners = (card: LeastOwnedCard) => {
    if (card.owners_count === 0) return 'Nessun possessore';
    const names = card.owners
      .slice(0, 2)
      .map((o) => o.nickname_forum ?? o.username)
      .join(', ');
    const extra = card.owners_count > 2 ? ` +${card.owners_count - 2}` : '';
    return names + extra;
  };

  return (
    <Container>
      <PageHeader>
        <PageTitle>Introvabili</PageTitle>
        <PageSub>Le 25 carte meno possedute dalla community</PageSub>
      </PageHeader>

      {loading && <LoadingText>Caricamento...</LoadingText>}

      {!loading && (
        <Grid>
          {cards.map((card) => {
            const src = toImgSrc(card.fronte_path);
            const rColor = RARITY_COLOR[card.rarity];
            return (
              <CardWrapper
                key={card.id}
                onClick={() => openDetail(card)}
                style={{ cursor: loadingDetailId === card.id ? 'wait' : 'pointer' }}
              >
                <RarityBar $color={rColor} />
                <CardImgArea>
                  <OwnersCountBadge $zero={card.owners_count === 0}>
                    {card.owners_count} poss.
                  </OwnersCountBadge>
                  {src
                    ? <CardImg src={src} alt={`Carta #${card.id}`} />
                    : <CardImgPlaceholder>#{card.id}</CardImgPlaceholder>
                  }
                </CardImgArea>
                <CardInfo>
                  <div>
                    <RarityDot $color={rColor} />
                    <RarityText $color={rColor}>{card.rarity}</RarityText>
                  </div>
                  <OwnersList>{formatOwners(card)}</OwnersList>
                </CardInfo>
              </CardWrapper>
            );
          })}
        </Grid>
      )}

      {detailCard && (
        <CardDetailModal
          card={detailCard}
          ownedQuantity={getOwnedQty(cards.find((c) => c.id === detailCard.id)!)}
          isAuthenticated={isAuthenticated}
          onClose={() => setDetailCard(null)}
        />
      )}
    </Container>
  );
};

export default LeastOwnedCardsMobile;
